import React, { useEffect, useState } from "react";
import { useParams } from "react-router-dom";
import { motion, AnimatePresence } from "framer-motion";
import api from "@/lib/api";
import KidShell from "@/components/KidShell";
import ReadAloud from "@/components/ReadAloud";
import Confetti from "@/components/Confetti";
import { ASSETS } from "@/lib/assets";
import { Wind, Flower2, BookOpen, Compass, Heart, Sparkles } from "lucide-react";

const KIND_WORDS = ["You are brave!", "God loves you!", "You are kind!", "You can do it!", "You are safe!", "You shine!", "Good job!", "You matter!"];

function WorryBubbles({ childId }) {
  const [bubbles, setBubbles] = useState([]);
  const [bursts, setBursts] = useState([]);
  const [popped, setPopped] = useState(0);
  const [word, setWord] = useState("");
  const playfieldRef = React.useRef(null);
  const burstTimersRef = React.useRef([]);

  useEffect(() => {
    const id = setInterval(() => {
      setBubbles((b) => [...b, { id: Math.random(), x: 10 + Math.random() * 80, size: 50 + Math.random() * 50 }].slice(-8));
    }, 900);
    const timers = burstTimersRef.current;
    return () => {
      clearInterval(id);
      timers.forEach((timer) => clearTimeout(timer));
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const pop = async (event, id) => {
    const field = playfieldRef.current?.getBoundingClientRect();
    const bubble = event.currentTarget.getBoundingClientRect();
    const burst = {
      id: `${Date.now()}-${Math.random()}`,
      x: field ? bubble.left - field.left + bubble.width / 2 : bubble.left,
      y: field ? bubble.top - field.top + bubble.height / 2 : bubble.top,
    };
    setBursts((items) => [...items, burst]);
    burstTimersRef.current.push(setTimeout(() => {
      setBursts((items) => items.filter((item) => item.id !== burst.id));
    }, 700));
    setBubbles((b) => b.filter((x) => x.id !== id));
    setWord(KIND_WORDS[Math.floor(Math.random() * KIND_WORDS.length)]);
    const n = popped + 1;
    setPopped(n);
    if (n === 5) {
      try { await api.post(`/children/${childId}/activities`, { type: "game", detail: "Worry Bubbles" }); }
      catch (e) { console.error("Failed to log activity:", e); }
    }
  };

  return (
    <div ref={playfieldRef} className="relative h-[60vh] overflow-hidden rounded-3xl bg-gradient-to-b from-[#CDEAFE] to-[#E8F7FF]">
      <p className="absolute top-3 left-0 right-0 text-center font-fredoka font-bold text-[#1D3557] z-10">Pop the worry bubbles! 🫧 Popped: {popped}</p>
      <AnimatePresence>
        {bubbles.map((b) => (
          <motion.button key={b.id} data-testid="worry-bubble" aria-label="Pop worry bubble" onClick={(event) => pop(event, b.id)}
            initial={{ y: "65vh", opacity: 0 }} animate={{ y: "-20vh", opacity: 1 }} exit={{ scale: 1.6, opacity: 0 }}
            transition={{ duration: 7, ease: "linear" }}
            className="absolute rounded-full border-2 border-white/90 bg-gradient-to-br from-white/90 via-[#BDE0FE]/80 to-[#8ECAE6]/75 shadow-md"
            style={{ left: `${b.x}%`, width: b.size, height: b.size }}>
            <span aria-hidden="true" className="absolute left-[22%] top-[16%] h-[24%] w-[18%] rotate-12 rounded-full bg-white/90" />
          </motion.button>
        ))}
      </AnimatePresence>
      <AnimatePresence>
        {bursts.map((burst) => (
          <div key={burst.id} data-testid="bubble-confetti" aria-hidden="true" className="pointer-events-none absolute z-20" style={{ left: burst.x, top: burst.y }}>
            {Array.from({ length: 14 }).map((_, index) => {
              const angle = (index / 14) * Math.PI * 2;
              const distance = 30 + ((index * 17) % 35);
              const colors = ["#FFB703", "#F94144", "#52B788", "#9B5DE5", "#F9844A", "#457B9D"];
              return (
                <motion.span
                  key={`${burst.id}-${index}`}
                  initial={{ x: -4, y: -4, scale: 1, opacity: 1, rotate: 0 }}
                  animate={{
                    x: Math.cos(angle) * distance,
                    y: Math.sin(angle) * distance,
                    scale: 0.45,
                    opacity: 0,
                    rotate: 180 + index * 35,
                  }}
                  transition={{ duration: 0.65, ease: "easeOut" }}
                  className="absolute block h-2.5 w-1.5 rounded-sm"
                  style={{ backgroundColor: colors[index % colors.length] }}
                />
              );
            })}
          </div>
        ))}
      </AnimatePresence>
      <AnimatePresence>
        {word && (
          <motion.div key={word} initial={{ scale: 0 }} animate={{ scale: 1 }} exit={{ opacity: 0 }}
            onAnimationComplete={() => setTimeout(() => setWord(""), 700)}
            className="absolute inset-0 flex items-center justify-center pointer-events-none">
            <span className="font-fredoka text-3xl font-bold text-[#457B9D] bg-white/80 rounded-full px-6 py-3">{word}</span>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
}

function GratitudeGarden({ childId }) {
  const [items, setItems] = useState([]);
  const [val, setVal] = useState("");
  const FLOWERS = ["🌸", "🌻", "🌷", "🌼", "🌹", "💐"];

  const add = async () => {
    if (!val.trim()) return;
    const next = [...items, { id: Math.random().toString(36).slice(2), text: val.trim() }];
    setItems(next);
    setVal("");
    if (next.length === 3) {
      try { await api.post(`/children/${childId}/activities`, { type: "game", detail: "Gratitude Garden" }); }
      catch (e) { console.error("Failed to log activity:", e); }
    }
  };

  return (
    <div className="rounded-3xl bg-gradient-to-b from-[#D8F3DC] to-[#F0FFF4] p-6">
      <p className="font-fredoka text-xl font-bold text-[#1D3557] text-center mb-1">My Thankful Garden 🌱</p>
      <p className="text-center text-[#457B9D] mb-4">What are you thankful for today? Plant a flower for each one!</p>
      <div className="flex gap-2 mb-4">
        <input data-testid="gratitude-input" value={val} onChange={(e) => setVal(e.target.value)} onKeyDown={(e) => e.key === "Enter" && add()}
          placeholder="I am thankful for..." className="flex-1 rounded-full border-2 border-white bg-white px-4 py-3 text-lg outline-none" />
        <button data-testid="gratitude-add" onClick={add} className="rounded-full bg-[#52b788] px-6 font-bold text-white">Plant 🌷</button>
      </div>
      <div className="min-h-[30vh] flex flex-wrap content-end gap-3 justify-center">
        {items.map((it, i) => (
          <motion.div key={i} initial={{ y: 30, opacity: 0 }} animate={{ y: 0, opacity: 1 }}
            data-testid="gratitude-flower" className="flex flex-col items-center">
            <span className="text-5xl">{FLOWERS[i % FLOWERS.length]}</span>
            <span className="text-sm font-semibold text-[#1D3557] max-w-[120px] text-center">{it}</span>
          </motion.div>
        ))}
      </div>
    </div>
  );
}

const JESUS_QUEST_STOPS = [
  {
    id: "welcome",
    title: "The Welcome",
    reference: "Mark 10:13–16",
    prompt: "A group of children came near Jesus. What do you notice about the way he welcomes them?",
    story: "Jesus made room for the children and showed everyone that each child matters. You belong in the story too.",
    accent: "from-[#FFE8C2] to-[#FFF8E8]",
    discoveries: [
      { id: "smile", emoji: "😊", label: "Share a smile", response: "A smile can help someone feel safe and seen." },
      { id: "welcome", emoji: "👋", label: "Make room", response: "Jesus welcomed people who others might overlook." },
    ],
  },
  {
    id: "storm",
    title: "Peace in the Storm",
    reference: "Mark 4:35–41",
    prompt: "The boat rocked and the friends felt afraid. Where could you look for peace?",
    story: "When the storm grew loud, Jesus brought calm. When your feelings feel stormy, you can pause, breathe, and ask for help.",
    accent: "from-[#CDEAFE] to-[#EEF9FF]",
    discoveries: [
      { id: "breathe", emoji: "🌬️", label: "Take a breath", response: "Slow breaths can help your body remember that you are safe right now." },
      { id: "calm", emoji: "🌊", label: "Find calm", response: "Jesus cares about your worries and stays close through them." },
    ],
  },
  {
    id: "sharing",
    title: "The Sharing Picnic",
    reference: "John 6:1–13",
    prompt: "One child shared a small lunch. What can a small act of kindness do?",
    story: "A small lunch became enough for a whole crowd when people trusted Jesus and shared what they had.",
    accent: "from-[#D8F3DC] to-[#F3FFF5]",
    discoveries: [
      { id: "bread", emoji: "🍞", label: "Find the bread", response: "Small gifts can become big blessings when they are shared." },
      { id: "basket", emoji: "🧺", label: "Fill the basket", response: "Your kindness counts, even when it feels small." },
    ],
  },
];

function WalkWithJesus({ childId }) {
  const [stopIndex, setStopIndex] = useState(0);
  const [found, setFound] = useState([]);
  const [showQuestion, setShowQuestion] = useState(false);
  const [answer, setAnswer] = useState(null);
  const [complete, setComplete] = useState(false);
  const [celebrate, setCelebrate] = useState(false);
  const activityLogged = React.useRef(false);
  const stop = JESUS_QUEST_STOPS[stopIndex];
  const allFound = found.length === stop.discoveries.length;

  const discover = (id) => {
    if (found.includes(id)) return;
    setFound((items) => [...items, id]);
  };

  const continueQuest = () => {
    if (!allFound) return;
    if (stopIndex < JESUS_QUEST_STOPS.length - 1) {
      setStopIndex((index) => index + 1);
      setFound([]);
      return;
    }
    setShowQuestion(true);
  };

  const chooseAnswer = async (isCorrect) => {
    setAnswer(isCorrect ? "correct" : "try-again");
    if (!isCorrect || activityLogged.current) return;
    activityLogged.current = true;
    setComplete(true);
    setCelebrate(true);
    try {
      await api.post(`/children/${childId}/activities`, { type: "game", detail: "Walk With Jesus Quest" });
    } catch (e) {
      console.error("Failed to log activity:", e);
    }
  };

  return (
    <div className="relative overflow-hidden rounded-3xl bg-white/80 shadow-sm">
      <Confetti show={celebrate} />
      <div className="flex items-center justify-between gap-3 border-b border-[#E8EEF4] px-5 py-4 sm:px-7">
        <div>
          <p className="flex items-center gap-2 font-fredoka text-xl font-bold text-[#1D3557]"><Compass className="h-6 w-6 text-[#F4A261]" /> Walk With Jesus</p>
          <p className="text-sm text-[#457B9D]">A kindness quest through three Bible stories</p>
        </div>
        <div className="rounded-full bg-[#FFF4D6] px-3 py-1 text-sm font-bold text-[#8A5A00]" data-testid="walk-progress">
          {complete ? "Quest complete!" : `${stopIndex + 1} / ${JESUS_QUEST_STOPS.length}`}
        </div>
      </div>

      {!showQuestion && (
        <AnimatePresence mode="wait">
          <motion.div key={stop.id} initial={{ opacity: 0, x: 20 }} animate={{ opacity: 1, x: 0 }} exit={{ opacity: 0, x: -20 }} className="p-5 sm:p-7">
            <div className={`relative overflow-hidden rounded-3xl bg-gradient-to-br ${stop.accent} p-5 sm:p-7`}>
              <div className="grid items-center gap-6 md:grid-cols-[0.8fr_1.2fr]">
                <div className="flex justify-center">
                  <div className="flex h-44 w-44 items-center justify-center rounded-full bg-white/70 p-3 shadow-sm sm:h-52 sm:w-52">
                    <img src={ASSETS.jesus} alt="Jesus welcoming children" className="h-full w-full object-contain" />
                  </div>
                </div>
                <div>
                  <p className="mb-1 text-sm font-bold uppercase tracking-[0.16em] text-[#8A5A00]">Story stop {stopIndex + 1}</p>
                  <h2 className="font-fredoka text-3xl font-bold text-[#1D3557]">{stop.title}</h2>
                  <p className="mt-1 text-sm font-semibold text-[#457B9D]">{stop.reference}</p>
                  <p className="mt-4 text-lg leading-relaxed text-[#1D3557]">{stop.story}</p>
                  <ReadAloud
                    text={`${stop.title}. ${stop.story}`}
                    label="Listen to the story"
                    testid={`walk-read-${stop.id}`}
                    className="mt-4"
                  />
                </div>
              </div>
            </div>

            <div className="mt-6">
              <p className="mb-3 text-center font-fredoka text-xl font-bold text-[#1D3557]">Explore this moment</p>
              <p className="mb-4 text-center text-[#457B9D]">Tap both discoveries to continue your quest.</p>
              <div className="grid gap-3 sm:grid-cols-2">
                {stop.discoveries.map((item) => {
                  const isFound = found.includes(item.id);
                  return (
                    <button
                      key={item.id}
                      data-testid={`walk-discovery-${item.id}`}
                      onClick={() => discover(item.id)}
                      className={`rounded-2xl border-2 p-4 text-left transition hover:-translate-y-0.5 ${isFound ? "border-[#52B788] bg-[#E8F8EE]" : "border-[#E6EEF4] bg-[#F8FBFD] hover:border-[#9BC7DF]"}`}
                    >
                      <span className="flex items-center gap-3">
                        <span className="text-3xl" aria-hidden="true">{item.emoji}</span>
                        <span className="flex-1 font-bold text-[#1D3557]">{item.label}</span>
                        {isFound && <Heart className="h-5 w-5 fill-[#52B788] text-[#52B788]" />}
                      </span>
                      {isFound && <span className="mt-2 block text-sm leading-relaxed text-[#457B9D]">{item.response}</span>}
                    </button>
                  );
                })}
              </div>
            </div>

            <button
              data-testid="walk-continue"
              onClick={continueQuest}
              disabled={!allFound}
              className="mt-6 flex w-full items-center justify-center gap-2 rounded-full bg-[#F4A261] px-5 py-3 font-bold text-white transition hover:bg-[#E98947] disabled:cursor-not-allowed disabled:opacity-45"
            >
              {stopIndex === JESUS_QUEST_STOPS.length - 1 ? "Finish the story" : "Continue the quest"} <span aria-hidden="true">→</span>
            </button>
          </motion.div>
        </AnimatePresence>
      )}

      {showQuestion && (
        <div className="p-5 sm:p-8">
          {!complete ? (
            <div className="mx-auto max-w-2xl text-center">
              <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-[#FFF4D6]">
                <Sparkles className="h-8 w-8 text-[#F4A261]" />
              </div>
              <p className="text-sm font-bold uppercase tracking-[0.16em] text-[#8A5A00]">Final quest choice</p>
              <h2 className="mt-2 font-fredoka text-3xl font-bold text-[#1D3557]">What will you carry with you?</h2>
              <p className="mt-3 text-lg text-[#457B9D]">{JESUS_QUEST_STOPS[0].prompt.replace("A group of children came near Jesus. What do you notice about the way he welcomes them?", "When a friend feels worried or left out, what can you remember from the stories?")}</p>
              <div className="mt-6 grid gap-3 text-left">
                <button data-testid="walk-quest-answer-correct" onClick={() => chooseAnswer(true)} className="rounded-2xl border-2 border-[#D8F3DC] bg-[#F3FFF5] p-4 font-semibold text-[#1D3557] transition hover:-translate-y-0.5">Jesus is near, and small acts of love can make a difference.</button>
                <button data-testid="walk-quest-answer-try-again" onClick={() => chooseAnswer(false)} className="rounded-2xl border-2 border-[#E6EEF4] bg-[#F8FBFD] p-4 font-semibold text-[#1D3557] transition hover:-translate-y-0.5">I should keep every kindness to myself.</button>
              </div>
              {answer === "try-again" && <p className="mt-4 font-semibold text-[#E76F51]">Think about the welcome, the calm, and the shared lunch. Try again! 💛</p>}
            </div>
          ) : (
            <div className="mx-auto max-w-2xl rounded-3xl bg-gradient-to-br from-[#FFF4D6] to-[#FFFDF4] p-7 text-center">
              <div className="text-6xl" aria-hidden="true">🌟</div>
              <h2 className="mt-3 font-fredoka text-3xl font-bold text-[#1D3557]">You finished the kindness quest!</h2>
              <p className="mt-3 text-lg leading-relaxed text-[#457B9D]">You discovered that every child matters, peace can grow in hard moments, and kindness is worth sharing.</p>
              <div className="mt-5 inline-flex items-center gap-2 rounded-full bg-white px-5 py-3 font-bold text-[#8A5A00] shadow-sm"><BookOpen className="h-5 w-5" /> Quest badge earned</div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

export default function Games() {
  const { childId } = useParams();
  const [child, setChild] = useState(null);
  const [game, setGame] = useState(null);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { api.get(`/children/${childId}`).then((r) => setChild(r.data)); }, [childId]);

  return (
    <KidShell child={child} title="Calm Games" bg="linear-gradient(180deg,#E6F4FF 0%,#FDFBF7 60%)">
      {!game ? (
        <div className="grid gap-5 sm:grid-cols-3">
          <button data-testid="game-bubbles" onClick={() => setGame("bubbles")}
            className="flex flex-col items-center gap-3 rounded-3xl bg-[#CDEAFE] p-8 shadow-sm transition hover:-translate-y-1">
            <Wind className="h-12 w-12 text-[#1D3557]" strokeWidth={2.5} />
            <span className="font-fredoka text-xl font-bold text-[#1D3557]">Worry Bubbles</span>
            <span className="text-sm text-[#457B9D] text-center">Pop your worries away</span>
          </button>
          <button data-testid="game-garden" onClick={() => setGame("garden")}
            className="flex flex-col items-center gap-3 rounded-3xl bg-[#D8F3DC] p-8 shadow-sm transition hover:-translate-y-1">
            <Flower2 className="h-12 w-12 text-[#1D3557]" strokeWidth={2.5} />
            <span className="font-fredoka text-xl font-bold text-[#1D3557]">Thankful Garden</span>
            <span className="text-sm text-[#457B9D] text-center">Grow a garden of gratitude</span>
          </button>
          <button data-testid="game-walk" onClick={() => setGame("walk")}
            className="flex flex-col items-center gap-3 rounded-3xl bg-[#FFF4D6] p-8 shadow-sm transition hover:-translate-y-1">
            <BookOpen className="h-12 w-12 text-[#1D3557]" strokeWidth={2.5} />
            <span className="font-fredoka text-xl font-bold text-[#1D3557]">Walk With Jesus</span>
            <span className="text-sm text-[#457B9D] text-center">Go on a kindness quest</span>
          </button>
        </div>
      ) : (
        <div>
          <button data-testid="games-back" onClick={() => setGame(null)} className="mb-3 font-semibold text-[#457B9D]">← Pick another game</button>
          {game === "bubbles" ? <WorryBubbles childId={childId} /> : game === "garden" ? <GratitudeGarden childId={childId} /> : <WalkWithJesus childId={childId} />}
        </div>
      )}
    </KidShell>
  );
}
